import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  websiteService,
  WebsiteServiceItem,
  WebsiteBlogPost,
} from '../../services/httpServices/websiteService';
import type { PaginationMeta } from '../../types/api';

/**
 * Query state for a website list. Filters, search and pagination are all
 * server-driven: the values here are sent to the API, which does the filtering
 * and slicing (and caches the response in Redis).
 */
export interface WebsiteQuery {
  page: number;
  limit: number;
  search: string;
  status: string;
  categoryId?: string;
}

interface ListState<T> {
  items: T[];
  meta: PaginationMeta | null;
  loading: boolean;
  error: string | null;
}

export interface WebsiteState {
  services: ListState<WebsiteServiceItem>;
  blogs: ListState<WebsiteBlogPost>;
  servicesQuery: WebsiteQuery;
  blogsQuery: WebsiteQuery;
  selectedServices: string[];
  selectedBlogs: string[];
}

const initialList = <T>(): ListState<T> => ({
  items: [],
  meta: null,
  loading: false,
  error: null,
});

const initialState: WebsiteState = {
  services: initialList<WebsiteServiceItem>(),
  blogs: initialList<WebsiteBlogPost>(),
  servicesQuery: { page: 1, limit: 10, search: '', status: '' },
  blogsQuery: { page: 1, limit: 10, search: '', status: '', categoryId: '' },
  selectedServices: [],
  selectedBlogs: [],
};

function errorMessage(error: any, fallback: string): string {
  return error?.response?.data?.message || error?.message || fallback;
}

// ── Services ────────────────────────────────────────────────
export const fetchWebsiteServices = createAsyncThunk(
  'website/fetchServices',
  async (query: WebsiteQuery, { rejectWithValue }) => {
    try {
      const res = await websiteService.getServices(
        query.page,
        query.limit,
        query.search || undefined,
        query.status || undefined,
      );
      return { items: res.data || [], meta: res.meta ?? null };
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, 'Failed to load website services'));
    }
  },
);

export const updateWebsiteService = createAsyncThunk(
  'website/updateService',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const res = await websiteService.updateService(id, data);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, 'Failed to update service'));
    }
  },
);

export const deleteWebsiteService = createAsyncThunk(
  'website/deleteService',
  async (id: string, { rejectWithValue }) => {
    try {
      await websiteService.deleteService(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, 'Failed to delete service'));
    }
  },
);

export const bulkDeleteWebsiteServices = createAsyncThunk(
  'website/bulkDeleteServices',
  async (ids: string[], { rejectWithValue }) => {
    try {
      await websiteService.bulkDeleteServices(ids);
      return ids;
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, 'Failed to delete selected services'));
    }
  },
);

// ── Blogs ───────────────────────────────────────────────────
export const fetchWebsiteBlogs = createAsyncThunk(
  'website/fetchBlogs',
  async (query: WebsiteQuery, { rejectWithValue }) => {
    try {
      const res = await websiteService.getBlogs(
        query.page,
        query.limit,
        query.search || undefined,
        query.status || undefined,
        query.categoryId || undefined,
      );
      return { items: res.data || [], meta: res.meta ?? null };
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, 'Failed to load blog posts'));
    }
  },
);

export const updateWebsiteBlog = createAsyncThunk(
  'website/updateBlog',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const res = await websiteService.updateBlog(id, data);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, 'Failed to update blog post'));
    }
  },
);

export const deleteWebsiteBlog = createAsyncThunk(
  'website/deleteBlog',
  async (id: string, { rejectWithValue }) => {
    try {
      await websiteService.deleteBlog(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, 'Failed to delete blog post'));
    }
  },
);

export const bulkDeleteWebsiteBlogs = createAsyncThunk(
  'website/bulkDeleteBlogs',
  async (ids: string[], { rejectWithValue }) => {
    try {
      await websiteService.bulkDeleteBlogs(ids);
      return ids;
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, 'Failed to delete selected blog posts'));
    }
  },
);

function withoutIds<T extends { id: string }>(items: T[], ids: string[]): T[] {
  const remove = new Set(ids);
  return items.filter((item) => !remove.has(item.id));
}

const websiteSlice = createSlice({
  name: 'website',
  initialState,
  reducers: {
    setServicesQuery: (state, action: PayloadAction<Partial<WebsiteQuery>>) => {
      state.servicesQuery = { ...state.servicesQuery, ...action.payload };
    },
    setBlogsQuery: (state, action: PayloadAction<Partial<WebsiteQuery>>) => {
      state.blogsQuery = { ...state.blogsQuery, ...action.payload };
    },
    toggleServiceSelected: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.selectedServices = state.selectedServices.includes(id)
        ? state.selectedServices.filter((value) => value !== id)
        : [...state.selectedServices, id];
    },
    setServicesSelected: (state, action: PayloadAction<string[]>) => {
      state.selectedServices = action.payload;
    },
    clearServicesSelection: (state) => {
      state.selectedServices = [];
    },
    toggleBlogSelected: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.selectedBlogs = state.selectedBlogs.includes(id)
        ? state.selectedBlogs.filter((value) => value !== id)
        : [...state.selectedBlogs, id];
    },
    setBlogsSelected: (state, action: PayloadAction<string[]>) => {
      state.selectedBlogs = action.payload;
    },
    clearBlogsSelection: (state) => {
      state.selectedBlogs = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Services: fetch ──
      .addCase(fetchWebsiteServices.pending, (state) => {
        state.services.loading = true;
        state.services.error = null;
      })
      .addCase(fetchWebsiteServices.fulfilled, (state, action) => {
        state.services.loading = false;
        state.services.items = action.payload.items;
        state.services.meta = action.payload.meta;
      })
      .addCase(fetchWebsiteServices.rejected, (state, action) => {
        state.services.loading = false;
        state.services.error = (action.payload as string) || 'Failed to load website services';
      })
      // ── Services: update ──
      .addCase(updateWebsiteService.fulfilled, (state, action) => {
        const updated = action.payload;
        state.services.items = state.services.items.map((item) =>
          item.id === updated.id ? { ...item, ...updated } : item,
        );
      })
      // ── Services: delete ──
      .addCase(deleteWebsiteService.fulfilled, (state, action) => {
        state.services.items = withoutIds(state.services.items, [action.payload]);
        state.selectedServices = state.selectedServices.filter((id) => id !== action.payload);
        if (state.services.meta) state.services.meta.total = Math.max(0, state.services.meta.total - 1);
      })
      // ── Services: bulk delete ──
      .addCase(bulkDeleteWebsiteServices.fulfilled, (state, action) => {
        state.services.items = withoutIds(state.services.items, action.payload);
        state.selectedServices = state.selectedServices.filter(
          (id) => !action.payload.includes(id),
        );
        if (state.services.meta) {
          state.services.meta.total = Math.max(0, state.services.meta.total - action.payload.length);
        }
      })
      // ── Blogs: fetch ──
      .addCase(fetchWebsiteBlogs.pending, (state) => {
        state.blogs.loading = true;
        state.blogs.error = null;
      })
      .addCase(fetchWebsiteBlogs.fulfilled, (state, action) => {
        state.blogs.loading = false;
        state.blogs.items = action.payload.items;
        state.blogs.meta = action.payload.meta;
      })
      .addCase(fetchWebsiteBlogs.rejected, (state, action) => {
        state.blogs.loading = false;
        state.blogs.error = (action.payload as string) || 'Failed to load blog posts';
      })
      // ── Blogs: update ──
      .addCase(updateWebsiteBlog.fulfilled, (state, action) => {
        const updated = action.payload;
        state.blogs.items = state.blogs.items.map((item) =>
          item.id === updated.id ? { ...item, ...updated } : item,
        );
      })
      // ── Blogs: delete ──
      .addCase(deleteWebsiteBlog.fulfilled, (state, action) => {
        state.blogs.items = withoutIds(state.blogs.items, [action.payload]);
        state.selectedBlogs = state.selectedBlogs.filter((id) => id !== action.payload);
        if (state.blogs.meta) state.blogs.meta.total = Math.max(0, state.blogs.meta.total - 1);
      })
      // ── Blogs: bulk delete ──
      .addCase(bulkDeleteWebsiteBlogs.fulfilled, (state, action) => {
        state.blogs.items = withoutIds(state.blogs.items, action.payload);
        state.selectedBlogs = state.selectedBlogs.filter((id) => !action.payload.includes(id));
        if (state.blogs.meta) {
          state.blogs.meta.total = Math.max(0, state.blogs.meta.total - action.payload.length);
        }
      });
  },
});

export const {
  setServicesQuery,
  setBlogsQuery,
  toggleServiceSelected,
  setServicesSelected,
  clearServicesSelection,
  toggleBlogSelected,
  setBlogsSelected,
  clearBlogsSelection,
} = websiteSlice.actions;

export default websiteSlice.reducer;
